import React, { useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { staffApi } from "../../lib/staff";
import { enqueueCheckin } from "../../lib/checkinQueue";
import { playAccept, playReject, playQueued } from "../../lib/sound";
import {
  CheckCircle2, AlertTriangle, XCircle, Ban, Clock,
  ScanLine, Keyboard, CameraOff, CloudOff, Undo2, Loader2,
} from "lucide-react";

const READER_ID = "st-staff-qr-reader";

const RESULT = {
  ok:                 { bg: "#14532d", ring: "#22c55e", title: "Checked in",         Icon: CheckCircle2 },
  warn_ok:            { bg: "#78350f", ring: "#f59e0b", title: "Checked in — date",  Icon: AlertTriangle },
  already_checked_in: { bg: "#78350f", ring: "#f59e0b", title: "Already checked in", Icon: AlertTriangle },
  wrong_date:         { bg: "#7f1d1d", ring: "#ef4444", title: "Wrong date",         Icon: Clock },
  ticket_cancelled:   { bg: "#7f1d1d", ring: "#ef4444", title: "Ticket cancelled",   Icon: Ban },
  wrong_attraction:   { bg: "#7f1d1d", ring: "#ef4444", title: "Wrong attraction",   Icon: XCircle },
  order_not_confirmed:{ bg: "#7f1d1d", ring: "#ef4444", title: "Not paid",           Icon: XCircle },
  not_found:          { bg: "#7f1d1d", ring: "#ef4444", title: "Ticket not found",   Icon: XCircle },
  queued:             { bg: "#1e3a8a", ring: "#60a5fa", title: "Saved offline",      Icon: CloudOff },
  undone:             { bg: "#1f2937", ring: "#9ca3af", title: "Check-in undone",     Icon: Undo2 },
};

function resultFromResponse(res) {
  if (res.ok) return { kind: res.warning ? "warn_ok" : "ok", sub: res.attendee?.name || "" };
  if (res.reason === "already_checked_in")
    return { kind: "already_checked_in", attendeeId: res.attendee?.id,
             sub: `${res.attendee?.name || ""} @ ${res.checked_in_at?.slice(11, 16) || ""}` };
  if (res.reason === "wrong_date")
    return { kind: "wrong_date", sub: `Ticket is for ${res.visit_date}, not today` };
  return { kind: res.reason || "not_found", sub: res.attendee?.name || "Try again" };
}

export default function StaffScanPage() {
  const { staff, refreshSummary } = useOutletContext();
  const device = staff?.device || "mobile-scan";

  const [status, setStatus] = useState("starting");
  const [undoing, setUndoing] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [result, setResult] = useState(null);
  const [count, setCount] = useState(0);
  const [showManual, setShowManual] = useState(false);
  const [manualVal, setManualVal] = useState("");

  const scannerRef = useRef(null);
  const busyRef = useRef(false);
  const recentRef = useRef({});
  const resultTimer = useRef(null);

  const showResult = (view, ok) => {
    if (ok) setCount((c) => c + 1);
    if (view.kind === "queued") playQueued();
    else if (ok) playAccept();
    else playReject();
    try { navigator.vibrate?.(ok ? 60 : [40, 60, 40]); } catch { /* noop */ }
    setResult(view);
    clearTimeout(resultTimer.current);
    // leave the "already checked in" card up longer so staff can tap Undo
    resultTimer.current = setTimeout(() => setResult(null), view.kind === "already_checked_in" ? 6500 : 2200);
  };

  const process = async (raw) => {
    const id = (raw || "").trim();
    if (!id || busyRef.current) return;
    const now = Date.now();
    if (recentRef.current[id] && now - recentRef.current[id] < 4000) return;
    for (const k of Object.keys(recentRef.current)) {
      if (now - recentRef.current[k] > 15000) delete recentRef.current[k];
    }
    recentRef.current[id] = now;
    busyRef.current = true;

    if (!navigator.onLine) {
      enqueueCheckin({ attendee_id: id, device });
      showResult({ kind: "queued", sub: "Will sync when back online" }, true);
      setTimeout(() => { busyRef.current = false; }, 1200);
      return;
    }

    try {
      const r = await staffApi.post("/checkin", { attendee_id: id, device, force: true });
      const res = r.data;
      showResult(resultFromResponse(res), res.ok);
      if (res.ok && refreshSummary) refreshSummary();
    } catch (err) {
      if (!err?.response) {
        // network dropped mid-request — queue it
        enqueueCheckin({ attendee_id: id, device });
        showResult({ kind: "queued", sub: "Will sync when back online" }, true);
      } else {
        showResult({ kind: "not_found", sub: "Try again" }, false);
      }
    }
    setTimeout(() => { busyRef.current = false; }, 1200);
  };

  const undoScan = async (attendeeId) => {
    setUndoing(true);
    try {
      await staffApi.post("/checkin/undo", { attendee_id: attendeeId, device });
      delete recentRef.current[attendeeId];  // let them scan straight back in
      showResult({ kind: "undone", sub: "They can scan in again" }, true);
      refreshSummary?.();
    } catch {
      showResult({ kind: "not_found", sub: "Undo failed — try again" }, false);
    } finally { setUndoing(false); }
  };

  const submitManual = (e) => {    e.preventDefault();
    const v = manualVal.trim();
    if (!v) return;
    setManualVal("");
    process(v);
  };

  useEffect(() => {
    let active = true;
    const initPromise = (async () => {
      const mod = await import("html5-qrcode");
      if (!active) return;
      const scanner = new mod.Html5Qrcode(READER_ID, { verbose: false });
      scannerRef.current = scanner;
      const qrbox = (vw, vh) => {
        const edge = Math.floor(Math.min(vw, vh) * 0.7);
        return { width: edge, height: edge };
      };
      await scanner.start({ facingMode: "environment" }, { fps: 10, qrbox }, (text) => process(text), () => {});
      if (!active) { try { await scanner.stop(); await scanner.clear(); } catch { /* noop */ } return; }
      setStatus("scanning");
    })().catch((e) => {
      if (!active) return;
      setStatus("error");
      const name = e?.name || "";
      if (name === "NotAllowedError") setErrMsg("Camera permission was denied. Allow camera access, then reload.");
      else if (name === "NotFoundError") setErrMsg("No camera found on this device.");
      else setErrMsg("Could not start the camera. You can still enter ticket IDs manually below.");
    });

    return () => {
      active = false;
      clearTimeout(resultTimer.current);
      (async () => {
        try { await initPromise; } catch { /* noop */ }
        const s = scannerRef.current;
        if (s) {
          try {
            if (s.getState && s.getState() === 2) await s.stop();
            await s.clear();
          } catch { /* noop */ }
        }
      })();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cfg = result ? RESULT[result.kind] : null;

  return (
    <div className="flex-1 flex flex-col min-h-0" data-testid="staff-scan-page">
      {/* Counter bar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-[#1a1a1a] bg-[#0c0c0c]">
        <div className="flex items-center gap-1.5 text-sm text-[#8a8a8a]">
          <ScanLine size={15} className="text-[#dc2626]" /> Scan tickets
        </div>
        <div className="flex items-center gap-1.5 text-sm font-semibold px-3 py-1 rounded-full bg-[#14532d]/40 border border-[#22c55e]/30" data-testid="staff-scan-count">
          <CheckCircle2 size={14} className="text-[#22c55e]" /> {count} in
        </div>
      </div>

      {/* Camera */}
      <div className="relative flex-1 flex items-center justify-center overflow-hidden bg-black min-h-[320px]">
        <div id={READER_ID} className="w-full max-w-[520px] mx-auto" />

        {status === "starting" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white/70">
            <ScanLine size={44} className="text-[#dc2626] animate-pulse" />
            <div>Starting camera…</div>
          </div>
        )}
        {status === "scanning" && !result && (
          <div className="pointer-events-none absolute inset-x-0 bottom-5 flex justify-center">
            <div className="flex items-center gap-2 text-sm text-white/80 bg-black/50 px-3 py-1.5 rounded-full">
              <ScanLine size={15} className="text-[#dc2626]" /> Point at a ticket QR
            </div>
          </div>
        )}
        {status === "error" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center px-8" data-testid="staff-scan-error">
            <CameraOff size={44} className="text-[#ef4444]" />
            <div className="text-white/80 max-w-xs">{errMsg}</div>
          </div>
        )}
        {result && cfg && (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center text-center px-8"
            style={{ background: cfg.bg }}
            data-testid={`staff-scan-result-${result.kind}`}
          >
            <cfg.Icon size={96} className="opacity-90" style={{ color: cfg.ring }} />
            <div className="text-3xl font-extrabold mt-4">{cfg.title}</div>
            {result.sub && <div className="text-lg text-white/85 mt-1.5">{result.sub}</div>}
            {result.kind === "already_checked_in" && result.attendeeId && (
              <button
                className="mt-6 px-4 py-2.5 rounded-xl bg-black/35 border border-white/25 text-sm font-semibold flex items-center gap-2 active:bg-black/50"
                onClick={() => undoScan(result.attendeeId)}
                disabled={undoing}
                data-testid="staff-scan-undo"
              >
                {undoing ? <Loader2 size={15} className="animate-spin" /> : <Undo2 size={15} />} Undo that check-in
              </button>
            )}
          </div>
        )}
      </div>

      {/* Manual */}
      <div className="border-t border-[#1a1a1a] bg-[#0c0c0c] p-3 space-y-2">
        <button className="st-btn w-full justify-center" onClick={() => setShowManual((s) => !s)} data-testid="staff-scan-toggle-manual">
          <Keyboard size={16} /> {showManual ? "Hide manual entry" : "Enter ticket ID manually"}
        </button>
        {showManual && (
          <form onSubmit={submitManual} className="flex gap-2" data-testid="staff-scan-manual-form">
            <input
              className="st-input flex-1"
              placeholder="Paste or type ticket ID"
              value={manualVal}
              onChange={(e) => setManualVal(e.target.value)}
              data-testid="staff-scan-manual-input"
            />
            <button type="submit" className="st-btn" data-testid="staff-scan-manual-submit">Check in</button>
          </form>
        )}
      </div>
    </div>
  );
}
